import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

/** Bucket por defecto: debe coincidir con `gsutil ls -p PROJECT_ID` (suele ser *.firebasestorage.app). */
function resolveStorageBucket() {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
  const raw = (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim();
  if (!projectId) return raw;
  if (!raw) return `${projectId}.firebasestorage.app`;
  if (raw === `${projectId}.appspot.com`) return `${projectId}.firebasestorage.app`;
  return raw;
}

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: resolveStorageBucket(),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};


const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');
const storage = getStorage(app);

/** Límite de la callable (payload ~9 MiB); imágenes más grandes: comprimir antes de subir. */
const MAX_IMAGE_BYTES_CALLABLE = 6 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.onload = () => {
      const s = reader.result;
      if (typeof s !== 'string') {
        reject(new Error('Formato de lectura no soportado.'));
        return;
      }
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Sube a Firebase Storage vía Cloud Function (Admin SDK).
 * El archivo sigue estando en tu bucket; se evita el 403 del SDK web con reglas de Storage.
 */
const uploadImage = async (file) => {
  if (!auth.currentUser) {
    throw new Error('Debes iniciar sesión como administrador para subir imágenes.');
  }
  if (!file.type.startsWith('image/')) {
    throw new Error('Por favor selecciona un archivo de imagen válido (JPG, PNG, GIF, etc.).');
  }
  if (file.size > MAX_IMAGE_BYTES_CALLABLE) {
    throw new Error(
      `La imagen supera ${MAX_IMAGE_BYTES_CALLABLE / (1024 * 1024)} MB. Reducí tamaño o calidad y probá de nuevo.`
    );
  }
  await auth.currentUser.getIdToken(true);

  const uid = auth.currentUser.uid;
  const userSnap = await getDoc(doc(db, 'users', uid));
  if (!userSnap.exists()) {
    throw new Error(
      `No existe users/${uid} en Firestore (mismo UID que en Authentication). Creá ese documento con role: "admin" o isAdmin: true.`
    );
  }
  const ud = userSnap.data();
  const roleOk = ud?.role === 'admin' || ud?.role === 'Admin';
  const flagOk = ud?.isAdmin === true;
  if (!roleOk && !flagOk) {
    throw new Error(
      `Para subir archivos, en users/${uid} necesitás role "admin" o isAdmin true. Ahora: role=${JSON.stringify(ud?.role)} isAdmin=${ud?.isAdmin}`
    );
  }

  const base64 = await fileToBase64(file);
  const callable = httpsCallable(functions, 'uploadSiteImage');
  try {
    const result = await callable({
      base64,
      contentType: file.type,
      originalName: file.name
    });
    const url = result.data?.downloadUrl;
    if (!url || typeof url !== 'string') {
      throw new Error('La función no devolvió la URL de la imagen.');
    }
    return url;
  } catch (err) {
    const code = err?.code;
    const msg = err?.message || '';
    if (code === 'functions/unauthenticated') {
      throw new Error('Sesión inválida. Volvé a iniciar sesión.');
    }
    if (code === 'functions/permission-denied') {
      throw new Error(msg || 'No tenés permiso para subir.');
    }
    if (code === 'functions/invalid-argument') {
      throw new Error(msg || 'Archivo no válido.');
    }
    if (code === 'functions/not-found') {
      throw new Error(
        'Falta desplegar la función uploadSiteImage: en la carpeta del proyecto ejecutá firebase deploy --only functions:uploadSiteImage'
      );
    }
    throw err instanceof Error ? err : new Error(String(msg || 'Error al subir la imagen.'));
  }
};

/**
 * Panel historial (admin): generar QR en la orden o reenviar correo con los mismos QR.
 * @param {'generateQrs' | 'resendPurchaseEmail'} action
 */
export async function adminOrderQrToolkitCall(orderId, action) {
  if (!auth.currentUser) {
    throw new Error('Iniciá sesión como administrador.');
  }
  await auth.currentUser.getIdToken(true);
  const callable = httpsCallable(functions, 'adminOrderQrToolkit');
  try {
    const result = await callable({ orderId, action });
    return result.data;
  } catch (err) {
    const code = err?.code;
    const msg = err?.message || '';
    if (code === 'functions/unauthenticated') {
      throw new Error('Sesión inválida. Volvé a iniciar sesión.');
    }
    if (code === 'functions/permission-denied' || code === 'functions/failed-precondition') {
      throw new Error(msg || 'No se pudo completar la acción.');
    }
    if (code === 'functions/not-found') {
      throw new Error('Orden no encontrada o función no desplegada (adminOrderQrToolkit).');
    }
    throw err instanceof Error ? err : new Error(String(msg || 'Error en la operación.'));
  }
}

export { auth, db, functions, storage, uploadImage };
